import { useMemo } from 'react';
import { useQuery } from 'react-query';
import { getCommonCodes } from 'apis';
import { CommonCodeCategory } from 'interfaces/common';

export interface CommonCodeOption {
  label: string;
  value: string;
}

interface GetOptionsConfig {
  withAll?: boolean; // 맨 앞에 '전체' 옵션 추가
  allLabel?: string;
  allValue?: string;
}

// 공통코드 트리 조회 (거의 안 바뀌는 데이터 → 세션 1회 캐시)
export const useCommonCodes = () =>
  useQuery<CommonCodeCategory[]>(['getCommonCodes'], getCommonCodes, {
    staleTime: Infinity,
    cacheTime: Infinity,
    refetchOnWindowFocus: false
  });

// 특정 카테고리의 코드들을 antd Select 옵션으로 변환
export const getCommonCodeOptions = (
  categories: CommonCodeCategory[] | undefined,
  categoryId: string,
  config?: GetOptionsConfig
): CommonCodeOption[] => {
  const { withAll = false, allLabel = '전체', allValue = 'ALL' } = config || {};
  const codes = categories?.find((category) => category.categoryId === categoryId)?.codes ?? [];
  const options = codes.map(({ codeId, codeName }) => ({ label: codeName, value: codeId }));
  return withAll ? [{ label: allLabel, value: allValue }, ...options] : options;
};

// 카테고리 옵션을 바로 쓰는 편의 훅
export const useCommonCodeOptions = (categoryId: string, config?: GetOptionsConfig) => {
  const { withAll, allLabel, allValue } = config || {};
  const { data, ...rest } = useCommonCodes();
  const options = useMemo(
    () => getCommonCodeOptions(data, categoryId, { withAll, allLabel, allValue }),
    [data, categoryId, withAll, allLabel, allValue]
  );
  return { options, ...rest };
};

// 특정 카테고리의 코드값 → 표시명 매핑
export const getCommonCodeNameMap = (
  categories: CommonCodeCategory[] | undefined,
  categoryId: string
): Record<string, string> => {
  const codes = categories?.find((category) => category.categoryId === categoryId)?.codes ?? [];
  const nameMap: Record<string, string> = {};
  codes.forEach(({ codeId, codeName }) => {
    nameMap[codeId] = codeName;
  });
  return nameMap;
};

// 코드값 → 표시명 매핑을 바로 쓰는 편의 훅 (코드로 받은 값을 화면에 표시할 때)
export const useCommonCodeNameMap = (categoryId: string) => {
  const { data, ...rest } = useCommonCodes();
  const nameMap = useMemo(() => getCommonCodeNameMap(data, categoryId), [data, categoryId]);
  return { nameMap, ...rest };
};
